import { createApi } from "@reduxjs/toolkit/query/react";
import { Params } from "custom-types";
import {
  BookingCreateDTO,
  BookingCreateResponse,
  BookingDetailResponse,
  PaymentCreateDTO,
  PaymentInitiateResponse,
  PaymentMethodsResponse,
  PaymentStatusDTO,
  PaymentStatusResponse,
  TicketGenerateDTO,
  VoucherValidateDTO,
  VoucherValidateResponse,
} from "types/api";
import { axiosBaseQuery } from "@picovico/helpers";
import { getLocalStorageData } from "@picovico/utils";

export const bookingApi = createApi({
  baseQuery: axiosBaseQuery(),
  tagTypes: ["Booking"],
  reducerPath: "bookingApi",
  endpoints: (builder) => ({
    generateTicket: builder.mutation<unknown, TicketGenerateDTO>({
      query: (params) => ({
        responseType: "blob",
        name: "generate-ticket",
        method: "GET",
        params,
        headers: {
          cinemahall: params?.cinemahall || getLocalStorageData("cinema_hall"),
        },
      }),
    }),
    createSeatBooking: builder.mutation<
      BookingCreateResponse,
      BookingCreateDTO
    >({
      query: (data) => ({
        name: "proceed_booking",
        method: "POST",
        data,
      }),
    }),
    getPaymentMethods: builder.query<PaymentMethodsResponse, Params>({
      query: (params) => {
        return {
          name: "payment_methods",
          method: "GET",
          params,
        };
      },
    }),
    getBookingDetail: builder.query<BookingDetailResponse, Params>({
      query: (queryData) => ({
        name: "booking_detail",
        method: "GET",
        params: queryData,
      }),
    }),
    reserveSeat: builder.mutation<BookingCreateResponse, unknown>({
      query: (data) => ({
        name: "reserve_seat",
        method: "POST",
        data,
      }),
    }),
    paymentInitiate: builder.mutation<
      PaymentInitiateResponse,
      PaymentCreateDTO
    >({
      query: (data) => ({
        name: "payment_initiate",
        method: "POST",
        data,
      }),
    }),
    getPaymentStatus: builder.mutation<PaymentStatusResponse, PaymentStatusDTO>(
      {
        query: ({ company_idx, ...data }) => ({
          name: "payment_status",
          method: "POST",
          params: {
            company_idx: company_idx,
          },
          data,
        }),
      },
    ),
    /**
     * Price a discount code without spending it.
     *
     * A preview only: amphitheater forwards this to dolby, which evaluates every rule (expiry,
     * movie, weekday, usage) but takes no hold on the code. The hold happens at payment
     * initiation, so trying three codes to see which is best does not lock all three.
     */
    validateVoucher: builder.mutation<VoucherValidateResponse, VoucherValidateDTO>({
      query: (data) => ({
        name: "voucher_validate",
        method: "POST",
        data,
      }),
    }),
    getFoodPaymentMethods: builder.query<PaymentMethodsResponse, Params>({
      query: ({cinema_hall, ...params}) => ({
        name: "food_payment_methods",
        method: "GET",
        params,
        headers: {
          cinemahall: cinema_hall,
        },
      }),
    }),
  }),
});

export const {
  useCreateSeatBookingMutation,
  useLazyGetPaymentMethodsQuery,
  useLazyGetBookingDetailQuery,
  usePaymentInitiateMutation,
  useGetPaymentStatusMutation,
  useReserveSeatMutation,
  useGenerateTicketMutation,
  useLazyGetFoodPaymentMethodsQuery,
  useValidateVoucherMutation,
} = bookingApi;
